import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowRight, Bell } from "lucide-react";
import { useAuth } from "../../utils/auth";
import { canAccessPanel, isOwner } from "../../utils/roles";
import {
  fetchNotificationDevices,
  fetchNotificationPreferences,
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  updateNotificationPreference,
} from "../../api/NotificationAPIs";
import { enableOrderNotifications, pushEnabledLocally } from "../../notifications/pushNotifications";

function timeAgo(value) {
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.max(0, Math.floor(diff / 60000));
  if (minutes < 1) return "الآن";
  if (minutes < 60) return `منذ ${minutes} دقيقة`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `منذ ${hours} ساعة`;
  return `منذ ${Math.floor(hours / 24)} يوم`;
}

function severityClass(severity) {
  if (severity === "CRITICAL") return "border-rose-300";
  if (severity === "WARNING") return "border-amber-300";
  return "border-slate-200";
}

export default function NotificationCenterPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const owner = isOwner(authState.role);
  const [page, setPage] = useState(0);
  const [pushState, setPushState] = useState(pushEnabledLocally() ? "granted" : "idle");

  useEffect(() => {
    if (!canAccessPanel(authState.role)) navigate("/login");
  }, [authState.role, navigate]);

  const listQuery = useQuery({
    queryKey: ["notifications", page],
    queryFn: () => fetchNotifications({ page, pageSize: 20 }),
    enabled: !!authState.token,
  });

  const preferencesQuery = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: fetchNotificationPreferences,
    enabled: owner && !!authState.token,
  });

  const devicesQuery = useQuery({
    queryKey: ["notification-devices"],
    queryFn: fetchNotificationDevices,
    enabled: owner && !!authState.token,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
    queryClient.invalidateQueries({ queryKey: ["notification-unread"] });
    queryClient.invalidateQueries({ queryKey: ["notification-preview"] });
  };

  const openItem = async (item) => {
    if (!item.isRead) {
      await markNotificationRead(item.notificationId);
      refresh();
    }
    if (item.actionUrl) navigate(item.actionUrl);
  };

  const markAll = async () => {
    await markAllNotificationsRead();
    refresh();
  };

  const togglePreference = async (type, enabled) => {
    try {
      await updateNotificationPreference(type, enabled);
      queryClient.invalidateQueries({ queryKey: ["notification-preferences"] });
    } catch (error) {
      toast.error(error.message || "تعذر حفظ الإعداد");
    }
  };

  const enablePush = async () => {
    try {
      const result = await enableOrderNotifications();
      setPushState(result.status);
      if (result.status === "granted") {
        toast.success("تم تفعيل إشعارات هذا الجهاز");
        queryClient.invalidateQueries({ queryKey: ["notification-devices"] });
      } else if (result.status === "denied") {
        toast.error("تم رفض الإذن من المتصفح.");
      } else if (result.status === "missing-vapid") {
        toast.error("مفتاح Web Push غير مضاف في إعدادات لوحة الإدارة.");
      } else if (result.status === "unsupported") {
        toast.error("هذا المتصفح لا يدعم إشعارات الويب.");
      }
    } catch (error) {
      toast.error(error.message || "تعذر تسجيل الجهاز");
    }
  };

  const items = listQuery.data?.items || [];
  const total = listQuery.data?.totalCount || 0;
  const pageSize = listQuery.data?.pageSize || 20;

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      <ToastContainer position="top-center" />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowRight size={16} />
            لوحة التحكم
          </button>
          <button type="button" onClick={markAll} className="text-sm text-indigo-600 hover:text-indigo-800">
            تعليم الكل كمقروء
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
            <Bell size={18} className="text-indigo-600" />
            <h1 className="font-extrabold text-slate-900">مركز الإشعارات</h1>
          </div>
          {listQuery.isLoading && <p className="px-5 py-8 text-sm text-slate-500">جارٍ التحميل...</p>}
          {!listQuery.isLoading && items.length === 0 && (
            <p className="px-5 py-8 text-sm text-slate-500">لا توجد إشعارات.</p>
          )}
          {items.map((item) => (
            <button
              key={item.notificationId}
              type="button"
              onClick={() => openItem(item)}
              className={`w-full text-right px-5 py-4 border-b border-slate-100 border-r-4 ${severityClass(item.severity)} ${
                item.isRead ? "bg-white" : "bg-slate-50"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <p className="font-semibold text-slate-900">{item.title}</p>
                <span className="text-xs text-slate-400 shrink-0">{timeAgo(item.createdAtUtc)}</span>
              </div>
              <p className="text-sm text-slate-600 mt-1">{item.body}</p>
              {!item.isRead && <p className="text-[11px] text-indigo-600 mt-2">غير مقروء</p>}
            </button>
          ))}
          {total > pageSize && (
            <div className="flex items-center justify-between px-5 py-3 text-sm">
              <button type="button" disabled={page === 0} onClick={() => setPage((value) => value - 1)} className="disabled:opacity-40">
                السابق
              </button>
              <button
                type="button"
                disabled={(page + 1) * pageSize >= total}
                onClick={() => setPage((value) => value + 1)}
                className="disabled:opacity-40"
              >
                التالي
              </button>
            </div>
          )}
        </div>

        {owner && (
          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-extrabold text-slate-900">إعدادات الإشعارات</h2>
                <p className="text-xs text-slate-500 mt-1">تُحفظ على الخادم لحساب المالك. إيقاف النوع يوقف الإشعار الجديد، والسجل السابق يبقى.</p>
              </div>
              <button
                type="button"
                onClick={enablePush}
                className="shrink-0 rounded-xl bg-indigo-600 text-white text-sm font-semibold px-4 py-2 hover:bg-indigo-700"
              >
                {pushState === "granted" ? "تحديث جهاز الإشعارات" : "تفعيل إشعارات الطلبات"}
              </button>
            </div>

            <div className="divide-y divide-slate-100">
              {(preferencesQuery.data || []).map((preference) => (
                <label key={preference.type} className="flex items-center justify-between gap-3 py-3">
                  <span className="text-sm text-slate-800">{preference.label}</span>
                  <input
                    type="checkbox"
                    checked={!!preference.enabled}
                    onChange={(event) => togglePreference(preference.type, event.target.checked)}
                    className="h-4 w-4 accent-indigo-600"
                  />
                </label>
              ))}
            </div>

            <div className="text-xs text-slate-500">
              الأجهزة المسجلة: {(devicesQuery.data || []).filter((device) => device.isActive).length}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
